// components/TenancyCourseForm.jsx
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Button,
    Grid,
    Typography,
    Card,
    CardMedia,
    CardContent,
    Autocomplete,
    TextField,
    Chip,
    Switch,
    IconButton,
    Tooltip,
    Paper,
    Alert,
    Modal
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import HelpIcon from '@mui/icons-material/HelpOutline';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import MainCard from '../../components/cards/MainCard';
import TrimmedTextField from '../../components/TrimmedTextField';
import TenancySelectionComponent from '../../components/TenancySelectionComponent';
import CardComponent from '../../components/cards/CourseCard';
import CoursePreview from '../courses/CoursePreview';
import { COURSE_CATEGORIES, APP_URL } from '../../config';
import { getCardColor } from '../../utils/colorUtils';
import { useTheme } from '@mui/material/styles';
import { formatDateOnly } from '../../utils/formatDateTime';

TenancyCourseForm.propTypes = {
    formData: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    pendingFiles: PropTypes.object.isRequired,
    onFileChange: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    isProcessing: PropTypes.bool,
    mode: PropTypes.oneOf(['create', 'edit']),
    existingImageUrl: PropTypes.string,
    existingGifUrl: PropTypes.string,
    existingCourseContentPath: PropTypes.string,
    courseId: PropTypes.string,
};

export default function TenancyCourseForm({
    formData,
    onChange,
    pendingFiles,
    onFileChange,
    onSubmit,
    isProcessing = false,
    mode = 'create',
    existingImageUrl,
    existingGifUrl,
    existingCourseContentPath,
    courseId,
}) {
    const theme = useTheme();
    const lightMode = theme.palette.mode === 'light';
    const courseLanguageOptions = COURSE_CATEGORIES.split(',');
    // Topical category (distinct from the LMS/SCORM technical type). Drives the
    // reports-b "Course Type" column. Kept in sync with reports-b COURSE_TYPES.
    const courseTagOptions = ['Cybersecurity', 'Information Security', 'Workplace Practices', 'Policies', 'Compliance'];
    const fileInputRef = useRef(null);
    const user = JSON.parse(localStorage.getItem("user"));
    const isAdmin = user.role === "Administrators";

    const [filePreviews, setFilePreviews] = useState({
        image: existingImageUrl || null,
        gif: existingGifUrl || null,
    });

    const [isDragging, setIsDragging] = useState(false);
    const [previewModalOpen, setPreviewModalOpen] = useState(false);

    // Determine if course content exists
    const hasExistingContent = mode === 'edit' && existingCourseContentPath;
    const hasPendingContent = pendingFiles.content !== undefined;

    useEffect(() => {
        // Create object URLs for preview
        const newPreviews = {};

        if (pendingFiles.image) {
            newPreviews.image = URL.createObjectURL(pendingFiles.image);
        } else if (existingImageUrl) {
            newPreviews.image = existingImageUrl;
        }

        if (pendingFiles.gif) {
            newPreviews.gif = URL.createObjectURL(pendingFiles.gif);
        } else if (existingGifUrl) {
            newPreviews.gif = existingGifUrl;
        }

        setFilePreviews(newPreviews);

        // Cleanup
        return () => {
            if (pendingFiles.image && newPreviews.image?.startsWith('blob:')) {
                URL.revokeObjectURL(newPreviews.image);
            }
            if (pendingFiles.gif && newPreviews.gif?.startsWith('blob:')) {
                URL.revokeObjectURL(newPreviews.gif);
            }
        };
    }, [pendingFiles.image, pendingFiles.gif, existingImageUrl, existingGifUrl]);

    const handleFileSelect = (event, fileType) => {
        const file = event.target.files[0];
        if (file) {
            onFileChange({ ...pendingFiles, [fileType]: file });
        }
        // Reset input value to allow selecting the same file again
        event.target.value = '';
    };

    const handleRemoveFile = (fileType) => {
        const newFiles = { ...pendingFiles };
        delete newFiles[fileType];
        onFileChange(newFiles);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit();
    };

    // Drag and drop handlers
    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            const file = files[0];
            // Check if it's a ZIP file
            if (
                file.name.toLowerCase().endsWith('.zip') ||
                file.type === 'application/zip' ||
                file.type === 'application/x-zip-compressed'
            ) {
                onFileChange({ ...pendingFiles, content: file });
            } else {
                alert('Please upload a ZIP file');
            }
        }
    };

    // Preview logic
    const canPreview = mode === 'edit' && existingCourseContentPath && !pendingFiles.content;
    const needsToSave = mode === 'create' || (mode === 'edit' && hasPendingContent);

    const handlePreview = () => {
        if (canPreview && courseId) {
            setPreviewModalOpen(true);
        }
    };

    const handleClosePreviewModal = () => {
        setPreviewModalOpen(false);
    };

    // Get preview message
    const getPreviewMessage = () => {
        if (mode === 'create' && !hasPendingContent) {
            return {
                severity: 'info',
                message: 'Upload course content above to enable preview after saving'
            };
        }
        if (mode === 'create' && hasPendingContent) {
            return {
                severity: 'warning',
                message: 'Click "Create Course" to save and enable preview'
            };
        }
        if (mode === 'edit' && hasPendingContent) {
            return {
                severity: 'warning',
                message: 'Click "Update Course" to preview your new content'
            };
        }
        if (mode === 'edit' && !hasPendingContent && existingCourseContentPath) {
            return {
                severity: 'success',
                message: 'Click the "Preview" button to view your course'
            };
        }
        return {
            severity: 'info',
            message: 'Upload course content to enable preview'
        };
    };

    const previewMessage = getPreviewMessage();

    // Default images
    const DEFAULT_GIF = '/default.gif';
    const DEFAULT_IMG = '/default.png';

    // Determine which image/gif to show in preview
    const previewImage = filePreviews.image || DEFAULT_IMG;
    const previewGif = filePreviews.gif || DEFAULT_GIF;

    // Course Content Upload Component (to be reused)
    const CourseContentUpload = () => (
        <MainCard
            content={true}
            boxShadow={true}
            border={false}
            sx={{ bgcolor: getCardColor(lightMode) }}
        >
            <Typography variant="h4" gutterBottom>
                Course Content (ZIP File)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {mode === "create" ? "Upload a ZIP file containing your course content. Must include an index.html file." :
                    "This course already has content uploaded. Adding a new ZIP file will replace the current content"}
            </Typography>
            {/* Drag and Drop Area - Compact when content exists */}
            <Paper
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                sx={{
                    border: `2px dashed ${theme.palette.primary.main}`,
                    borderRadius: 2,
                    p: hasExistingContent && !hasPendingContent ? 2 : 4,
                    textAlign: 'center',
                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                    bgcolor: isDragging
                        ? theme.palette.primary.lighter
                        : hasExistingContent && !hasPendingContent
                            ? getCardColor(lightMode)
                            : 'transparent',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        bgcolor: isProcessing
                            ? 'transparent'
                            : theme.palette.action.hover,
                    },
                    display: 'flex',
                    flexDirection: hasExistingContent && !hasPendingContent ? 'row' : 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: hasExistingContent && !hasPendingContent ? 2 : 0,
                }}
            >
                <CloudUploadIcon
                    sx={{
                        fontSize: hasExistingContent && !hasPendingContent ? 32 : 48,
                        color: theme.palette.primary.main,
                        mb: hasExistingContent && !hasPendingContent ? 0 : 2,
                    }}
                />

                <Box sx={{
                    textAlign: hasExistingContent && !hasPendingContent ? 'left' : 'center',
                    flex: hasExistingContent && !hasPendingContent ? 1 : 'none'
                }}>
                    <Typography
                    >
                        {hasPendingContent
                            ? pendingFiles.content.name
                            : hasExistingContent
                                ? 'Content uploaded - Click to replace'
                                : 'Drag and drop your ZIP file here'}
                    </Typography>
                    {(!hasExistingContent || hasPendingContent) && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {hasExistingContent && !hasPendingContent
                                ? 'Click to upload new content'
                                : 'or click to browse'}
                        </Typography>
                    )}
                </Box>

                <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    accept=".zip,application/zip,application/x-zip-compressed"
                    onChange={(e) => handleFileSelect(e, 'content')}
                    disabled={isProcessing}
                />
            </Paper>

            {hasPendingContent && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                    <UploadFileIcon color="primary" />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                        {pendingFiles.content.name}
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={() => handleRemoveFile('content')}
                        disabled={isProcessing}
                    >
                        <DeleteIcon />
                    </IconButton>
                </Box>
            )}
        </MainCard>
    );

    return (
        <>
            <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    {/* CREATE MODE - Single Column Layout */}
                    {mode === 'create' && (
                        <Grid item xs={12} md={6}>
                            <Grid container spacing={3}>
                                {/* Course Name */}
                                <Grid item xs={12}>
                                    <TrimmedTextField
                                        label="Course Name"
                                        value={formData.courseName || ''}
                                        onChange={(e) => onChange({ ...formData, courseName: e.target.value })}
                                        fullWidth
                                        required
                                        disabled={isProcessing}
                                    />
                                </Grid>

                                {/* Languages */}
                                <Grid item xs={12}>
                                    <Autocomplete
                                        multiple
                                        options={courseLanguageOptions}
                                        value={formData.languageCodes || []}
                                        onChange={(_, newValue) =>
                                            onChange({ ...formData, languageCodes: newValue })
                                        }
                                        disabled={isProcessing}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Languages" />
                                        )}
                                        renderTags={(value, getTagProps) =>
                                            value.map((option, index) => (
                                                <Chip
                                                    variant="outlined"
                                                    size="small"
                                                    label={option}
                                                    {...getTagProps({ index })}
                                                    key={option}
                                                />
                                            ))
                                        }
                                    />
                                </Grid>

                                {/* Category: topical course type; drives the reports-b "Course Type" column. */}
                                <Grid item xs={12}>
                                    <Autocomplete
                                        options={courseTagOptions}
                                        value={formData.tag || null}
                                        onChange={(_, newValue) =>
                                            onChange({ ...formData, tag: newValue || '' })
                                        }
                                        disabled={isProcessing}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Category" />
                                        )}
                                    />
                                </Grid>

                                {/* LMS Settings - Super admin only*/}
                                {isAdmin && (
                                    <Grid item xs={12}>
                                        <MainCard
                                            content={true}
                                            boxShadow={true}
                                            border={false}
                                            sx={{ bgcolor: getCardColor(lightMode, formData.isLms) }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Typography variant="h4">LMS Course</Typography>
                                                    <Tooltip
                                                        title="Enable this if the course is an LMS course"
                                                        placement="top"
                                                    >
                                                        <IconButton size="small">
                                                            <HelpIcon sx={{ color: 'secondary.main' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                                <Switch
                                                    checked={formData.isLms || false}
                                                    onChange={(e) =>
                                                        onChange({ ...formData, isLms: e.target.checked })
                                                    }
                                                    disabled={isProcessing}
                                                />
                                            </Box>
                                            <Typography variant="caption">
                                                {formData.isLms
                                                    ? 'This course is marked as an LMS course'
                                                    : 'This course is not an LMS course'}
                                            </Typography>
                                        </MainCard>
                                    </Grid>
                                )}

                                {/* Visibility Settings - super admin only */}
                                {isAdmin && (
                                    <Grid item xs={12}>
                                        <MainCard
                                            content={true}
                                            boxShadow={true}
                                            border={false}
                                            sx={{ bgcolor: getCardColor(lightMode, formData.published) }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Typography variant="h4">Course Visibility</Typography>
                                                    <Tooltip
                                                        title="When enabled, the course will be visible to users according to sharing settings"
                                                        placement="top"
                                                    >
                                                        <IconButton size="small">
                                                            <HelpIcon sx={{ color: 'secondary.main' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                                <Switch
                                                    checked={formData.published || false}
                                                    onChange={(e) =>
                                                        onChange({ ...formData, published: e.target.checked })
                                                    }
                                                    disabled={isProcessing}
                                                />
                                            </Box>
                                            <Typography variant="caption">
                                                {formData.published
                                                    ? 'The course is visible to users according to sharing settings'
                                                    : 'The course is currently hidden from all users'}
                                            </Typography>
                                        </MainCard>
                                    </Grid>
                                )}

                                {/* Sharing Settings - super Admin Version */}
                                {isAdmin && (
                                    <Grid item xs={12}>
                                        <MainCard
                                            content={true}
                                            boxShadow={true}
                                            border={false}
                                            sx={{ bgcolor: getCardColor(lightMode) }}
                                        >
                                            <Typography variant="h4" sx={{ mb: 2 }}>
                                                Course Sharing Settings
                                            </Typography>

                                            <TenancySelectionComponent
                                                selectedTenancies={formData.sharedTenancyIds || []}
                                                onChange={(tenancyIds) =>
                                                    onChange({ ...formData, sharedTenancyIds: tenancyIds })
                                                }
                                                disabled={isProcessing}
                                            />

                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    p: 1,
                                                    mt: 2,
                                                    bgcolor: getCardColor(lightMode),
                                                    borderRadius: 1,
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Typography variant="body1">Share with Tenancy Family</Typography>
                                                    <Tooltip
                                                        title="When enabled, this course will be accessible to all descendants of the selected tenancies"
                                                        placement="top"
                                                    >
                                                        <IconButton size="small">
                                                            <HelpIcon sx={{ color: 'secondary.main' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                                <Switch
                                                    checked={formData.publishToFamily || false}
                                                    onChange={(e) =>
                                                        onChange({ ...formData, publishToFamily: e.target.checked })
                                                    }
                                                    disabled={
                                                        isProcessing || formData.sharedTenancyIds?.includes(0)
                                                    }
                                                />
                                            </Box>

                                            {formData.sharedTenancyIds?.includes(0) && (
                                                <Typography variant="caption">
                                                    "Family sharing" is disabled because the course is shared with all
                                                    tenancies.
                                                </Typography>
                                            )}
                                        </MainCard>
                                    </Grid>
                                )}

                                {/* Sharing Settings - Non-Admin Version */}
                                {!isAdmin && (
                                    <Grid item xs={12}>
                                        <MainCard
                                            content={true}
                                            boxShadow={true}
                                            border={false}
                                            sx={{ bgcolor: getCardColor(lightMode) }}
                                        >

                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    p: 1,
                                                    bgcolor: getCardColor(lightMode),
                                                    borderRadius: 1,
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Typography variant="body1">Share with Tenancy Family</Typography>
                                                    <Tooltip
                                                        title="When enabled, this course will be accessible to all descendants of the selected tenancies"
                                                        placement="top"
                                                    >
                                                        <IconButton size="small">
                                                            <HelpIcon sx={{ color: 'secondary.main' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                                <Switch
                                                    checked={formData.publishToFamily || false}
                                                    onChange={(e) =>
                                                        onChange({ ...formData, publishToFamily: e.target.checked })
                                                    }
                                                    disabled={
                                                        isProcessing || formData.sharedTenancyIds?.includes(0)
                                                    }
                                                />
                                            </Box>
                                        </MainCard>
                                    </Grid>
                                )}

                                {/* Course Content Upload - in create mode */}
                                <Grid item xs={12}>
                                    <CourseContentUpload />
                                </Grid>

                                {/* Submit Button */}
                                <Grid item xs={12}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="warning"
                                        fullWidth
                                        size="large"
                                        disabled={
                                            isProcessing ||
                                            !formData.courseName ||
                                            !formData.languageCodes?.length
                                        }
                                    >
                                        Create Course
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}

                    {/* EDIT MODE - Two Column Layout */}
                    {mode === 'edit' && (
                        <>
                            {/* LEFT COLUMN */}
                            <Grid item xs={12} md={6}>
                                <Grid container spacing={3}>
                                    {/* Course Name */}
                                    <Grid item xs={12}>
                                        <TrimmedTextField
                                            label="Course Name"
                                            value={formData.courseName || ''}
                                            onChange={(e) => onChange({ ...formData, courseName: e.target.value })}
                                            fullWidth
                                            required
                                            disabled={isProcessing || mode === 'edit'}
                                            helperText='Course name cannot be changed after creation'
                                        />
                                    </Grid>

                                    {/* Languages */}
                                    <Grid item xs={12}>
                                        <Autocomplete
                                            multiple
                                            options={courseLanguageOptions}
                                            value={formData.languageCodes || []}
                                            onChange={(_, newValue) =>
                                                onChange({ ...formData, languageCodes: newValue })
                                            }
                                            disabled={isProcessing}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Languages" />
                                            )}
                                            renderTags={(value, getTagProps) =>
                                                value.map((option, index) => (
                                                    <Chip
                                                        variant="outlined"
                                                        size="small"
                                                        label={option}
                                                        {...getTagProps({ index })}
                                                        key={option}
                                                    />
                                                ))
                                            }
                                        />
                                    </Grid>

                                    {/* LMS Settings - Super admin only*/}
                                    {isAdmin && (
                                        <Grid item xs={12}>
                                            <MainCard
                                                content={true}
                                                boxShadow={true}
                                                border={false}
                                                sx={{ bgcolor: getCardColor(lightMode, formData.isLms) }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Typography variant="h4">LMS Course</Typography>
                                                        <Tooltip
                                                            title="Enable this if the course is an LMS course"
                                                            placement="top"
                                                        >
                                                            <IconButton size="small">
                                                                <HelpIcon sx={{ color: 'secondary.main' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                    <Switch
                                                        checked={formData.isLms || false}
                                                        onChange={(e) =>
                                                            onChange({ ...formData, isLms: e.target.checked })
                                                        }
                                                        disabled={isProcessing}
                                                    />
                                                </Box>
                                                <Typography variant="caption">
                                                    {formData.isLms
                                                        ? 'This course is marked as an LMS course'
                                                        : 'This course is not an LMS course'}
                                                </Typography>
                                            </MainCard>
                                        </Grid>
                                    )}

                                    {/* Visibility Settings - super admin only */}
                                    {isAdmin && (
                                        <Grid item xs={12}>
                                            <MainCard
                                                content={true}
                                                boxShadow={true}
                                                border={false}
                                                sx={{ bgcolor: getCardColor(lightMode, formData.published) }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Typography variant="h4">Course Visibility</Typography>
                                                        <Tooltip
                                                            title="When enabled, the course will be visible to users according to sharing settings"
                                                            placement="top"
                                                        >
                                                            <IconButton size="small">
                                                                <HelpIcon sx={{ color: 'secondary.main' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                    <Switch
                                                        checked={formData.published || false}
                                                        onChange={(e) =>
                                                            onChange({ ...formData, published: e.target.checked })
                                                        }
                                                        disabled={isProcessing}
                                                    />
                                                </Box>
                                                <Typography variant="caption">
                                                    {formData.published
                                                        ? 'The course is visible to users according to sharing settings'
                                                        : 'The course is currently hidden from all users'}
                                                </Typography>
                                            </MainCard>
                                        </Grid>
                                    )}

                                    {/* Sharing Settings - super Admin Version */}
                                    {isAdmin && (
                                        <Grid item xs={12}>
                                            <MainCard
                                                content={true}
                                                boxShadow={true}
                                                border={false}
                                                sx={{ bgcolor: getCardColor(lightMode) }}
                                            >
                                                <Typography variant="h4" sx={{ mb: 2 }}>
                                                    Course Sharing Settings
                                                </Typography>

                                                <TenancySelectionComponent
                                                    selectedTenancies={formData.sharedTenancyIds || []}
                                                    onChange={(tenancyIds) =>
                                                        onChange({ ...formData, sharedTenancyIds: tenancyIds })
                                                    }
                                                    disabled={isProcessing}
                                                />

                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        p: 1,
                                                        mt: 2,
                                                        bgcolor: getCardColor(lightMode),
                                                        borderRadius: 1,
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Typography variant="body1">Share with Tenancy Family</Typography>
                                                        <Tooltip
                                                            title="When enabled, this course will be accessible to all descendants of the selected tenancies"
                                                            placement="top"
                                                        >
                                                            <IconButton size="small">
                                                                <HelpIcon sx={{ color: 'secondary.main' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                    <Switch
                                                        checked={formData.publishToFamily || false}
                                                        onChange={(e) =>
                                                            onChange({ ...formData, publishToFamily: e.target.checked })
                                                        }
                                                        disabled={
                                                            isProcessing || formData.sharedTenancyIds?.includes(0)
                                                        }
                                                    />
                                                </Box>

                                                {formData.sharedTenancyIds?.includes(0) && (
                                                    <Typography variant="caption">
                                                        "Family sharing" is disabled because the course is shared with all
                                                        tenancies.
                                                    </Typography>
                                                )}
                                            </MainCard>
                                        </Grid>
                                    )}

                                    {/* Sharing Settings - Non-Admin Version */}
                                    {!isAdmin && (
                                        <Grid item xs={12}>
                                            <MainCard
                                                content={true}
                                                boxShadow={true}
                                                border={false}
                                                sx={{ bgcolor: getCardColor(lightMode) }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        bgcolor: getCardColor(lightMode),
                                                        borderRadius: 1,
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Typography variant="body1">Share with Tenancy Family</Typography>
                                                        <Tooltip
                                                            title="When enabled, this course will be accessible to all descendants of the selected tenancies"
                                                            placement="top"
                                                        >
                                                            <IconButton size="small">
                                                                <HelpIcon sx={{ color: 'secondary.main' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                    <Switch
                                                        checked={formData.publishToFamily || false}
                                                        onChange={(e) =>
                                                            onChange({ ...formData, publishToFamily: e.target.checked })
                                                        }
                                                        disabled={
                                                            isProcessing || formData.sharedTenancyIds?.includes(0)
                                                        }
                                                    />
                                                </Box>
                                            </MainCard>
                                        </Grid>
                                    )}

                                    {/* Course Media - Only in edit mode */}
                                    <Grid item xs={12}>
                                        <Typography variant="h4" gutterBottom>
                                            Course Media
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            Upload a course image (PNG) and/or animated preview (GIF)
                                        </Typography>
                                        <Alert severity="info" sx={{ mb: 3 }}>
                                            If no image or GIF is uploaded, default placeholders will be used automatically
                                        </Alert>

                                        <Grid container spacing={3}>
                                            {/* Image Upload */}
                                            <Grid item xs={6}>
                                                <Card sx={{ borderRadius: theme.borderRadius.sm, boxShadow: theme.customShadows.z1, bgcolor: getCardColor(lightMode) }}>
                                                    {filePreviews.image ? (
                                                        <CardMedia
                                                            component="img"
                                                            height="265"
                                                            image={filePreviews.image}
                                                            alt="Course Preview"
                                                            sx={{ objectFit: 'contain', bgcolor: 'action.hover' }}
                                                        />
                                                    ) : (
                                                        <Box
                                                            sx={{
                                                                height: 265,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                bgcolor: 'action.hover',
                                                            }}
                                                        >
                                                            <AddPhotoAlternateIcon
                                                                sx={{ fontSize: 60, color: 'action.disabled' }}
                                                            />
                                                        </Box>
                                                    )}
                                                    <CardContent>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                                mb: 1,
                                                            }}
                                                        >
                                                            <Typography variant="subtitle2">
                                                                Course Image (PNG)
                                                            </Typography>
                                                        </Box>
                                                        <Button
                                                            component="label"
                                                            variant="outlined"
                                                            fullWidth
                                                            disabled={isProcessing}
                                                        >
                                                            {pendingFiles.image || existingImageUrl
                                                                ? 'Change Image'
                                                                : 'Upload Image'}
                                                            <input
                                                                type="file"
                                                                hidden
                                                                accept="image/png"
                                                                onChange={(e) => handleFileSelect(e, 'image')}
                                                            />
                                                        </Button>
                                                    </CardContent>
                                                </Card>
                                            </Grid>

                                            {/* GIF Upload */}
                                            <Grid item xs={6}>
                                                <Card sx={{ borderRadius: theme.borderRadius.sm, boxShadow: theme.customShadows.z1, bgcolor: getCardColor(lightMode) }}>
                                                    {filePreviews.gif ? (
                                                        <CardMedia
                                                            component="img"
                                                            height="265"
                                                            image={filePreviews.gif}
                                                            alt="Course Animation"
                                                            sx={{ objectFit: 'contain', bgcolor: 'action.hover' }}
                                                        />
                                                    ) : (
                                                        <Box
                                                            sx={{
                                                                height: 265,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                bgcolor: 'action.hover',
                                                            }}
                                                        >
                                                            <AddPhotoAlternateIcon
                                                                sx={{ fontSize: 60, color: 'action.disabled' }}
                                                            />
                                                        </Box>
                                                    )}
                                                    <CardContent>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                                mb: 1,
                                                            }}
                                                        >
                                                            <Typography variant="subtitle2">
                                                                Course Animation (GIF)
                                                            </Typography>
                                                        </Box>
                                                        <Button
                                                            component="label"
                                                            variant="outlined"
                                                            fullWidth
                                                            disabled={isProcessing}
                                                        >
                                                            {pendingFiles.gif || existingGifUrl
                                                                ? 'Change GIF'
                                                                : 'Upload GIF'}
                                                            <input
                                                                type="file"
                                                                hidden
                                                                accept="image/gif"
                                                                onChange={(e) => handleFileSelect(e, 'gif')}
                                                            />
                                                        </Button>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    {/* Submit Button */}
                                    <Grid item xs={12}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            color="warning"
                                            fullWidth
                                            size="large"
                                            disabled={
                                                isProcessing ||
                                                !formData.courseName ||
                                                !formData.languageCodes?.length
                                            }
                                        >
                                            Update Course
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* RIGHT COLUMN - Only in edit mode */}
                            <Grid item xs={12} md={6}>
                                <Grid container spacing={3}>
                                    {/* Course Content Upload */}
                                    <Grid item xs={12}>
                                        <CourseContentUpload />
                                    </Grid>

                                    {/* Course Preview Section */}
                                    <Grid item xs={12}>
                                        <MainCard
                                            content={true}
                                            boxShadow={true}
                                            border={false}
                                            sx={{ bgcolor: getCardColor(lightMode) }}
                                        >
                                            <Typography variant="h4" gutterBottom>
                                                Course Preview
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                Preview how your course will appear to users
                                            </Typography>

                                            <Alert severity={previewMessage.severity} sx={{ mb: 2 }}>
                                                {previewMessage.message}
                                            </Alert>

                                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                                <Box sx={{ width: '100%', maxWidth: '350px' }}>
                                                    <CardComponent
                                                        image={previewImage}
                                                        title={formData.courseName || 'Course Name'}
                                                        gif={previewGif}
                                                        dateLabel="Created:"
                                                        dateValue={formatDateOnly(new Date(), 'dd-MM-yyyy')}
                                                        onClick={handlePreview}
                                                        type="preview"
                                                        customButtonText="Preview"
                                                        customButtonDisabled={!canPreview}
                                                    />
                                                </Box>
                                            </Box>
                                        </MainCard>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </>
                    )}
                </Grid>
            </form>

            {/* Preview Modal */}
            <Modal
                open={previewModalOpen}
                onClose={handleClosePreviewModal}
                aria-labelledby="course-preview-modal"
                aria-describedby="course-preview-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    borderRadius: '8px',
                    width: '90%',
                    maxWidth: '1200px',
                    height: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    {courseId && <CoursePreview courseId={courseId} />}
                </Box>
            </Modal>
        </>
    );
}